import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import JsonView from "@uiw/react-json-view"
import { githubDarkTheme } from "@uiw/react-json-view/githubDark"
import { githubLightTheme } from "@uiw/react-json-view/githubLight"
import { format } from "date-fns"
import {
	ArrowLeft,
	Braces,
	Check,
	Copy,
	CreditCard,
	ExternalLink,
	Globe,
	Package,
	RotateCcw,
	ShieldAlert,
	ShoppingCart,
	Wallet,
} from "lucide-react"
import { useState } from "react"
import { useTheme } from "@/components/global/theme/theme-provider"
import {
	DetailPanel,
	DetailPanelSection,
	KeyValueItem,
	KeyValueList,
	Panel,
	Section,
} from "@/components/layout"
import {
	AdminNotesPanel,
	OrderStatusBadge,
	RiskLevelBadge,
	StripeRiskBadge,
	TransactionStatusBadge,
} from "@/components/marketplace"
import { CopyableText } from "@/components/marketplace/copyable-text"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusDot, StatusTag } from "@/components/ui/status"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { SummaryGroup, SummaryRow } from "@/components/users/summary-panel"
import { stripeDashboardUrl } from "@/features/command-palette/external-links"
import { createTabSearch } from "@/hooks/use-url-tab"
import { useAdminTransactionDetail } from "@/queries/marketplace"
import type { AdminTransactionDetail } from "@/types/marketplace.types"

const TABS = ["details", "full-json"] as const
type TransactionTab = (typeof TABS)[number]

export const Route = createFileRoute("/transactions/$transactionId/")({
	component: TransactionDetailPage,
	validateSearch: createTabSearch(TABS),
})

// ============================================================================
// HELPERS
// ============================================================================

function formatCurrency(cents: number, currency: string): string {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: currency.toUpperCase(),
	}).format(cents / 100)
}

/** A negative amount: a real minus sign before the formatted value. */
function formatNegative(cents: number, currency: string): string {
	return `−${formatCurrency(cents, currency)}`
}

function getCountryFlag(countryCode: string): string {
	const codePoints = countryCode
		.toUpperCase()
		.split("")
		.map((char) => 127397 + char.charCodeAt(0))
	return String.fromCodePoint(...codePoints)
}

function formatDateTime(iso: string): string {
	return format(new Date(iso), "MMM d, yyyy 'at' h:mm a")
}

function Dot() {
	return <span className="text-meta">·</span>
}

function StripeLink({ id, label }: { id: string; label: string }) {
	return (
		<a
			href={stripeDashboardUrl(id)}
			target="_blank"
			rel="noopener noreferrer"
			aria-label={`Open ${label} in Stripe Dashboard`}
			title="Open in Stripe Dashboard"
			className="text-meta hover:text-primary-text"
		>
			<ExternalLink className="h-3 w-3" />
		</a>
	)
}

function SellerCell({
	seller,
}: {
	seller: AdminTransactionDetail["orders"][number]["seller"]
}) {
	return (
		<div className="flex items-center gap-2">
			<Avatar className="h-5 w-5">
				<AvatarImage src={seller.avatarUrl} />
				<AvatarFallback className="text-[10px]">
					{seller.username.slice(0, 2).toUpperCase()}
				</AvatarFallback>
			</Avatar>
			<span>@{seller.username}</span>
		</div>
	)
}

// ============================================================================
// PAGE
// ============================================================================

function TransactionDetailPage() {
	const { transactionId } = Route.useParams()
	const { transaction, isLoading, error } = useAdminTransactionDetail({
		transactionId,
	})
	const searchParams = Route.useSearch()
	const navigate = useNavigate()
	const activeTab = searchParams.tab ?? "details"

	const setActiveTab = (tab: string) => {
		navigate({
			to: "/transactions/$transactionId",
			params: { transactionId },
			search: tab === "details" ? {} : { tab: tab as TransactionTab },
			replace: true,
		})
	}

	if (isLoading) {
		return (
			<div className="flex flex-col gap-6 p-4 sm:p-6">
				<Skeleton className="h-14 w-full max-w-96" />
				<div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8">
					<div className="space-y-4">
						<Skeleton className="h-9 w-64" />
						<Skeleton className="h-40" />
						<Skeleton className="h-40" />
					</div>
					<Skeleton className="h-96" />
				</div>
			</div>
		)
	}

	if (error || !transaction) {
		return (
			<div className="flex h-[calc(100vh-3rem)] flex-col items-center justify-center gap-3 p-4 sm:p-6">
				<h2 className="text-lg font-semibold">Transaction not found</h2>
				<p className="text-bad">
					{error?.message ||
						`The transaction with ID ${transactionId} could not be found.`}
				</p>
				<Button variant="outline" asChild>
					<Link to="/transactions" search={{} as never}>
						<ArrowLeft />
						Back to transactions
					</Link>
				</Button>
			</div>
		)
	}

	return (
		<div className="flex flex-col gap-6 px-4 pt-4 pb-10 sm:px-6">
			<TransactionHeader transaction={transaction} />

			<div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8">
				<div className="min-w-0">
					<Tabs
						value={activeTab}
						onValueChange={setActiveTab}
						className="gap-6"
					>
						<TabsList variant="line">
							<TabsTrigger value="details">Details</TabsTrigger>
							<TabsTrigger
								value="full-json"
								className="flex items-center gap-2"
							>
								<Braces />
								Full JSON
							</TabsTrigger>
						</TabsList>

						<TabsContent value="details" className="mt-0">
							<div className="flex min-w-0 flex-col gap-4">
								<AdminNotesPanel
									targetType="transaction"
									targetId={transaction.id}
									variant="panel"
								/>
								<StripePanel transaction={transaction} />
								<BuyerContextPanel transaction={transaction} />
								<OrderRiskSummaryPanel transaction={transaction} />
								<AttachedOrdersPanel transaction={transaction} />
								<CartSnapshotPanel transaction={transaction} />
								{transaction.refunds.length > 0 && (
									<RefundHistoryPanel transaction={transaction} />
								)}
							</div>
						</TabsContent>

						<TabsContent value="full-json" className="mt-0">
							<Section
								label="Full transaction JSON"
								action={<CopyJsonButton data={transaction} />}
							>
								<Panel className="overflow-auto p-4">
									<TransactionJsonView data={transaction} />
								</Panel>
							</Section>
						</TabsContent>
					</Tabs>
				</div>

				<div className="lg:sticky lg:top-6">
					<TransactionSummary transaction={transaction} />
				</div>
			</div>
		</div>
	)
}

// ============================================================================
// HEADER
// ============================================================================

function TransactionHeader({
	transaction,
}: {
	transaction: AdminTransactionDetail
}) {
	return (
		<div className="flex flex-col gap-2">
			<Link
				to="/transactions"
				search={{} as never}
				className="inline-flex w-fit items-center gap-1 text-xs text-meta hover:text-foreground"
			>
				<ArrowLeft className="h-3 w-3" />
				Transactions
			</Link>
			<div className="flex flex-wrap items-center gap-x-3 gap-y-1">
				<h1 className="min-w-0">
					<CopyableText
						text={transaction.transactionNumber}
						className="text-xl font-semibold"
					/>
				</h1>
				<TransactionStatusBadge status={transaction.status} />
				<RiskLevelBadge riskLevel={transaction.riskLevel} />
			</div>
			<div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-muted-foreground">
				<Link
					to="/users/$userId"
					params={{ userId: transaction.buyer.id }}
					className="inline-flex items-center gap-1.5 text-foreground hover:text-primary-text"
				>
					<Avatar className="h-[18px] w-[18px]">
						<AvatarImage
							src={transaction.buyer.avatarUrl}
							alt={transaction.buyer.username}
						/>
						<AvatarFallback className="text-[9px]">
							{transaction.buyer.username.slice(0, 2).toUpperCase()}
						</AvatarFallback>
					</Avatar>
					@{transaction.buyer.username}
				</Link>
				<span className="text-meta">buyer</span>
				<Dot />
				<span className="font-medium text-foreground tabular-nums">
					{formatCurrency(transaction.total, transaction.currency)}
				</span>
				<Dot />
				<span>{transaction.region}</span>
				<Dot />
				<span>Created {formatDateTime(transaction.createdAt)}</span>
				{transaction.paidAt && (
					<>
						<Dot />
						<span>Paid {formatDateTime(transaction.paidAt)}</span>
					</>
				)}
			</div>
		</div>
	)
}

// ============================================================================
// SUMMARY
// ============================================================================

/**
 * Buyer fee waived and discount given by the buyer promotion applied at
 * checkout. A discount paid by CardNexus comes off the total; one paid by the
 * shop is already in the subtotal.
 */
function BuyerPromotionRows({
	promotion,
	currency,
}: {
	promotion: NonNullable<AdminTransactionDetail["buyerPromotion"]>
	currency: string
}) {
	const paidByCardNexus =
		promotion.discountAmount - promotion.shopDiscountAmount
	const name = promotion.id ? (
		<Link
			to="/buyer-promotions/$promotionId"
			params={{ promotionId: promotion.id }}
			className="underline-offset-2 hover:text-primary-text hover:underline"
		>
			{promotion.name}
		</Link>
	) : (
		promotion.name
	)

	return (
		<>
			{promotion.feeWaivedAmount > 0 && (
				<SummaryRow label={<>Buyer fee waived, {name}</>}>
					{formatCurrency(promotion.feeWaivedAmount, currency)}
				</SummaryRow>
			)}
			{promotion.shopDiscountAmount > 0 && (
				<SummaryRow
					label={<>Discount paid by the shop, {name} (in the subtotal)</>}
				>
					{formatCurrency(promotion.shopDiscountAmount, currency)}
				</SummaryRow>
			)}
			{paidByCardNexus > 0 && (
				<SummaryRow
					label={<>Discount paid by CardNexus, {name}</>}
					valueClassName="text-bad"
				>
					{formatNegative(paidByCardNexus, currency)}
				</SummaryRow>
			)}
		</>
	)
}

function TransactionSummary({
	transaction,
}: {
	transaction: AdminTransactionDetail
}) {
	const { currency } = transaction
	const subtotalAndShipping = transaction.subtotal + transaction.shippingTotal
	const couponLabel = `Coupon${transaction.couponCode ? ` (${transaction.couponCode})` : ""}`

	return (
		<DetailPanel title="Money" icon={Wallet}>
			<SummaryGroup label="Order">
				<SummaryRow label="Subtotal">
					{formatCurrency(transaction.subtotal, currency)}
				</SummaryRow>
				<SummaryRow label="Shipping">
					{formatCurrency(transaction.shippingTotal, currency)}
				</SummaryRow>
				<SummaryRow label="Order value" total>
					{formatCurrency(subtotalAndShipping, currency)}
				</SummaryRow>
			</SummaryGroup>

			<SummaryGroup label="Charged to the buyer">
				<SummaryRow
					label={`Buyer fee (${(transaction.buyerFee.percentage * 100).toFixed(1)}%)`}
				>
					{formatCurrency(transaction.buyerFee.total, currency)}
				</SummaryRow>
				{transaction.buyerPromotion && (
					<BuyerPromotionRows
						promotion={transaction.buyerPromotion}
						currency={currency}
					/>
				)}
				{transaction.couponDiscountAmount != null &&
					transaction.couponDiscountAmount > 0 && (
						<SummaryRow
							label={
								transaction.couponId ? (
									<Link
										to="/coupons/$couponId"
										params={{ couponId: transaction.couponId }}
										className="hover:text-primary-text hover:underline"
									>
										{couponLabel}
									</Link>
								) : (
									couponLabel
								)
							}
							valueClassName="text-bad"
						>
							{formatNegative(transaction.couponDiscountAmount, currency)}
						</SummaryRow>
					)}
				{transaction.taxAmount > 0 && (
					<SummaryRow label="Tax">
						{formatCurrency(transaction.taxAmount, currency)}
					</SummaryRow>
				)}
				<SummaryRow label="Total" total>
					{formatCurrency(transaction.total, currency)}
				</SummaryRow>
				{transaction.refundedAmount > 0 && (
					<SummaryRow label="Refunded" valueClassName="text-bad">
						{formatNegative(transaction.refundedAmount, currency)}
					</SummaryRow>
				)}
			</SummaryGroup>

			{transaction.stripeFees && (
				<SummaryGroup label="Stripe fees">
					<SummaryRow label="Total">
						{formatCurrency(
							transaction.stripeFees.amount,
							transaction.stripeFees.currency,
						)}
					</SummaryRow>
					{transaction.stripeFees.details?.map((detail) => (
						<SummaryRow
							key={`${detail.type}-${detail.amount}`}
							label={
								detail.type === "currency_conversion"
									? "Stripe FX conversion fee"
									: detail.description || detail.type
							}
							className="pl-3 text-xs"
						>
							{formatCurrency(detail.amount, detail.currency)}
						</SummaryRow>
					))}
				</SummaryGroup>
			)}

			{transaction.fxMarginPercent > 0 && (
				<SummaryGroup label="Currency conversion fee (internal)">
					<SummaryRow label="Currency conversion fee">
						{formatCurrency(
							// Base = subtotal + shipping (the seller's marked-up portion),
							// not the total, which includes the buyer fee and tax.
							Math.round(
								(transaction.subtotal + transaction.shippingTotal) *
									(transaction.fxMarginPercent /
										(1 + transaction.fxMarginPercent)),
							),
							currency,
						)}
					</SummaryRow>
					<SummaryRow label="Rate">
						{`${(transaction.fxMarginPercent * 100).toFixed(2)}%`}
					</SummaryRow>
				</SummaryGroup>
			)}
		</DetailPanel>
	)
}

// ============================================================================
// DETAIL SECTIONS
// ============================================================================

/** Stripe's side of the payment: the references, then Stripe's risk assessment when there is one. */
function StripePanel({ transaction }: { transaction: AdminTransactionDetail }) {
	return (
		<DetailPanel title="Stripe" icon={CreditCard}>
			<DetailPanelSection label="References">
				<StripeReferences transaction={transaction} />
			</DetailPanelSection>
			{transaction.stripeRisk && (
				<DetailPanelSection label="Risk assessment">
					<RiskAssessment risk={transaction.stripeRisk} />
				</DetailPanelSection>
			)}
		</DetailPanel>
	)
}

function StripeReferences({
	transaction,
}: {
	transaction: AdminTransactionDetail
}) {
	return (
		<KeyValueList>
			<KeyValueItem label="Checkout session">
				<span className="inline-flex max-w-full items-center gap-2">
					<CopyableText text={transaction.stripeCheckoutSessionId} />
					<StripeLink
						id={transaction.stripeCheckoutSessionId}
						label="Checkout Session"
					/>
				</span>
			</KeyValueItem>
			{transaction.stripeChargeId && (
				<KeyValueItem label="Charge">
					<span className="inline-flex max-w-full items-center gap-2">
						<CopyableText text={transaction.stripeChargeId} />
						<StripeLink id={transaction.stripeChargeId} label="Charge" />
					</span>
				</KeyValueItem>
			)}
			{transaction.stripePaymentIntentId && (
				<KeyValueItem label="Payment intent">
					<CopyableText text={transaction.stripePaymentIntentId} />
				</KeyValueItem>
			)}
			{transaction.stripeRisk?.cardFingerprint && (
				<KeyValueItem label="Card fingerprint">
					<CopyableText text={transaction.stripeRisk.cardFingerprint} />
				</KeyValueItem>
			)}
		</KeyValueList>
	)
}

function CountryValue({ country }: { country: string }) {
	return (
		<span>
			{getCountryFlag(country)} {country}
		</span>
	)
}

function BuyerContextPanel({
	transaction,
}: {
	transaction: AdminTransactionDetail
}) {
	if (!transaction.buyerContext) return null

	const ctx = transaction.buyerContext
	const cardCountry = transaction.stripeRisk?.cardCountry
	const deliveryCountry = transaction.deliveryCountry
	const ipCountry = ctx.country

	const countries = [ipCountry, deliveryCountry, cardCountry].filter(Boolean)
	const hasMismatch = new Set(countries).size > 1

	return (
		<DetailPanel
			title="Buyer context"
			icon={Globe}
			badges={
				hasMismatch ? (
					<StatusTag tone="warn">Country mismatch</StatusTag>
				) : undefined
			}
		>
			<DetailPanelSection>
				<KeyValueList>
					{ctx.ip && (
						<KeyValueItem label="IP">
							<CopyableText text={ctx.ip} />
						</KeyValueItem>
					)}
					{ipCountry && (
						<KeyValueItem label="IP country">
							<CountryValue country={ipCountry} />
						</KeyValueItem>
					)}
					{deliveryCountry && (
						<KeyValueItem label="Delivery country">
							<CountryValue country={deliveryCountry} />
						</KeyValueItem>
					)}
					{cardCountry && (
						<KeyValueItem label="Card country">
							<CountryValue country={cardCountry} />
						</KeyValueItem>
					)}
					{ctx.acceptLanguage && (
						<KeyValueItem label="Accept-Language">
							<span className="font-mono text-xs">{ctx.acceptLanguage}</span>
						</KeyValueItem>
					)}
					{ctx.userAgent && (
						<KeyValueItem label="User agent">
							<span className="text-xs break-all text-muted-foreground">
								{ctx.userAgent}
							</span>
						</KeyValueItem>
					)}
				</KeyValueList>
			</DetailPanelSection>
		</DetailPanel>
	)
}

function RiskAssessment({
	risk,
}: {
	risk: NonNullable<AdminTransactionDetail["stripeRisk"]>
}) {
	return (
		<KeyValueList>
			<KeyValueItem label="Risk level">
				<StripeRiskBadge
					riskLevel={risk.riskLevel}
					riskScore={risk.riskScore}
				/>
			</KeyValueItem>
			{risk.cardChecks?.cvcCheck && (
				<KeyValueItem label="CVC check">
					<CheckResult result={risk.cardChecks.cvcCheck} />
				</KeyValueItem>
			)}
			{risk.cardChecks?.addressLine1Check && (
				<KeyValueItem label="Address line 1">
					<CheckResult result={risk.cardChecks.addressLine1Check} />
				</KeyValueItem>
			)}
			{risk.cardChecks?.addressPostalCodeCheck && (
				<KeyValueItem label="Postal code">
					<CheckResult result={risk.cardChecks.addressPostalCodeCheck} />
				</KeyValueItem>
			)}
			{risk.outcome?.type && (
				<KeyValueItem label="3DS result">
					<span className="capitalize">{risk.outcome.type}</span>
				</KeyValueItem>
			)}
			{risk.outcome?.sellerMessage && (
				<KeyValueItem label="Seller message">
					<span className="text-muted-foreground">
						{risk.outcome.sellerMessage}
					</span>
				</KeyValueItem>
			)}
		</KeyValueList>
	)
}

function CheckResult({ result }: { result: string }) {
	if (result === "fail") {
		return <StatusTag tone="bad">{result}</StatusTag>
	}

	return (
		<StatusDot tone={result === "pass" ? "ok" : "neutral"}>{result}</StatusDot>
	)
}

function OrderRiskSummaryPanel({
	transaction,
}: {
	transaction: AdminTransactionDetail
}) {
	const ordersWithRisk = transaction.orders.filter(
		(o) => o.riskLevel && o.riskLevel !== "none",
	)

	if (ordersWithRisk.length === 0) return null

	return (
		<DetailPanel
			title="Order risk indicators"
			icon={ShieldAlert}
			badges={<RiskLevelBadge riskLevel={transaction.riskLevel} />}
			flush
		>
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Order</TableHead>
						<TableHead>Seller</TableHead>
						<TableHead>Risk</TableHead>
						<TableHead className="text-right">Signals</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{ordersWithRisk.map((order) => (
						<TableRow key={order.id}>
							<TableCell>
								<Link
									to="/orders/$orderId"
									params={{ orderId: order.orderNumber || order.id }}
									search={{ tab: "risk" }}
									className="font-mono text-foreground hover:text-primary-text"
								>
									{order.orderNumber || order.id.slice(0, 8)}
								</Link>
							</TableCell>
							<TableCell>
								<SellerCell seller={order.seller} />
							</TableCell>
							<TableCell>
								<RiskLevelBadge riskLevel={order.riskLevel} />
							</TableCell>
							<TableCell className="text-right tabular-nums">
								{order.riskIndicators?.length ?? 0}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</DetailPanel>
	)
}

function AttachedOrdersPanel({
	transaction,
}: {
	transaction: AdminTransactionDetail
}) {
	if (transaction.orders.length === 0) return null

	return (
		<DetailPanel
			title="Attached orders"
			icon={Package}
			meta={`${transaction.orders.length} order${transaction.orders.length === 1 ? "" : "s"}`}
			flush
		>
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Order</TableHead>
						<TableHead>Seller</TableHead>
						<TableHead>Status</TableHead>
						<TableHead className="text-right">Subtotal</TableHead>
						<TableHead className="text-right">Shipping</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{transaction.orders.map((order) => (
						<TableRow key={order.id}>
							<TableCell>
								<Link
									to="/orders/$orderId"
									params={{ orderId: order.orderNumber || order.id }}
									search={{ tab: undefined }}
									className="font-mono text-foreground hover:text-primary-text"
								>
									{order.orderNumber || order.id.slice(0, 8)}
								</Link>
							</TableCell>
							<TableCell>
								<SellerCell seller={order.seller} />
							</TableCell>
							<TableCell>
								<OrderStatusBadge status={order.status} />
							</TableCell>
							<TableCell className="text-right tabular-nums">
								{formatCurrency(order.subtotal, transaction.currency)}
							</TableCell>
							<TableCell className="text-right tabular-nums">
								{formatCurrency(order.shippingAmount, transaction.currency)}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</DetailPanel>
	)
}

function CartSnapshotPanel({
	transaction,
}: {
	transaction: AdminTransactionDetail
}) {
	if (transaction.cartSnapshot.length === 0) return null

	return (
		<DetailPanel
			title="Cart snapshot"
			icon={ShoppingCart}
			meta={`${transaction.cartSnapshot.length} item${transaction.cartSnapshot.length === 1 ? "" : "s"}`}
			flush
		>
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Product</TableHead>
						<TableHead className="text-right">Qty</TableHead>
						<TableHead className="text-right">Unit price</TableHead>
						<TableHead className="text-right">Line total</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{transaction.cartSnapshot.map((item) => (
						<TableRow key={item.inventoryLineId}>
							<TableCell
								className="font-mono text-muted-foreground"
								title={item.productId}
							>
								{item.productId.slice(0, 12)}…
							</TableCell>
							<TableCell className="text-right tabular-nums">
								{item.quantity}
							</TableCell>
							<TableCell className="text-right tabular-nums">
								{formatCurrency(item.unitPrice, transaction.currency)}
							</TableCell>
							<TableCell className="text-right tabular-nums">
								{formatCurrency(item.lineTotal, transaction.currency)}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</DetailPanel>
	)
}

function RefundHistoryPanel({
	transaction,
}: {
	transaction: AdminTransactionDetail
}) {
	return (
		<DetailPanel
			title="Refund history"
			icon={RotateCcw}
			meta={`${transaction.refunds.length} refund${transaction.refunds.length === 1 ? "" : "s"}`}
			flush
		>
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Refund</TableHead>
						<TableHead>Reason</TableHead>
						<TableHead className="text-right">Amount</TableHead>
						<TableHead>Date</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{transaction.refunds.map((refund) => (
						<TableRow key={refund.stripeRefundId}>
							<TableCell className="font-mono">
								{refund.stripeRefundId}
							</TableCell>
							<TableCell className="capitalize">
								{refund.reason.replace(/_/g, " ")}
							</TableCell>
							<TableCell className="text-right tabular-nums">
								{formatCurrency(refund.amount, transaction.currency)}
							</TableCell>
							<TableCell className="text-muted-foreground">
								{format(new Date(refund.createdAt), "MMM d, yyyy")}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</DetailPanel>
	)
}

// ============================================================================
// JSON VIEW
// ============================================================================

function TransactionJsonView({ data }: { data: object }) {
	const { resolvedTheme } = useTheme()
	const theme = resolvedTheme === "dark" ? githubDarkTheme : githubLightTheme

	return (
		<JsonView
			value={data}
			style={theme}
			collapsed={2}
			displayDataTypes={false}
		/>
	)
}

function CopyJsonButton({ data }: { data: unknown }) {
	const [copied, setCopied] = useState(false)

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(JSON.stringify(data, null, 2))
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		} catch {
			// Clipboard API unavailable or denied
		}
	}

	return (
		<Button variant="ghost" size="sm" onClick={handleCopy}>
			{copied ? <Check /> : <Copy />}
			{copied ? "Copied" : "Copy JSON"}
		</Button>
	)
}
